// Shared types, helpers, and small UI atoms for the SEO & Articles module.
// Kept separate so the section components and the admin shell (which renders the
// SEO sub-nav) draw from one source of truth.
import { ReactNode, useCallback, useRef, useState } from 'react';
import { fmtRelativeDate } from '../../../lib/format';

export type SeoTab =
  | 'articles' | 'keywords' | 'competitors' | 'ai'
  | 'links' | 'analytics' | 'integrations' | 'media' | 'settings';

// Single source for the SEO section nav — used by the sidebar sub-nav (admin
// shell) and the mobile tab bar (SeoModule).
export const SEO_TABS: { key: SeoTab; label: string; icon: string }[] = [
  { key: 'articles', label: 'Articles', icon: '📄' },
  { key: 'keywords', label: 'Keywords Research', icon: '🔍' },
  { key: 'competitors', label: 'Competitor Library', icon: '🏆' },
  { key: 'ai', label: 'AI Content Studio', icon: '🤖' },
  { key: 'links', label: 'Internal Links', icon: '🔗' },
  { key: 'analytics', label: 'SEO Analytics', icon: '📈' },
  { key: 'integrations', label: 'Integrations', icon: '🔌' },
  { key: 'media', label: 'Media Library', icon: '🖼' },
  { key: 'settings', label: 'SEO Settings', icon: '⚙️' },
];

// ── color helpers (mirror the design's lookups) ──────────────────────────────
export function statusStyle(s: string): { bg: string; fg: string } {
  const map: Record<string, [string, string]> = {
    Published: ['rgba(34,211,176,.14)', '#22d3b0'],
    Draft: ['rgba(239,162,58,.14)', '#efa23a'],
    Review: ['rgba(55,167,255,.14)', '#37a7ff'],
    'Needs Update': ['rgba(255,138,58,.14)', '#ff8a3a'],
    Archived: ['rgba(139,145,166,.14)', '#8b91a6'],
  };
  const [bg, fg] = map[s] || map.Draft;
  return { bg, fg };
}
export function scoreColor(n: number): string {
  return n >= 90 ? '#22d3b0' : n >= 80 ? '#37a7ff' : n >= 70 ? '#efa23a' : '#ff5d6c';
}
export function diffStyle(d: string): { bg: string; fg: string } {
  const map: Record<string, [string, string]> = {
    Low: ['rgba(34,211,176,.16)', '#22d3b0'],
    Medium: ['rgba(239,162,58,.16)', '#efa23a'],
    High: ['rgba(255,93,108,.16)', '#ff5d6c'],
  };
  const [bg, fg] = map[d] || map.Medium;
  return { bg, fg };
}
export function volColor(v: string): string {
  return v === 'High' ? '#22d3b0' : v === 'Medium' ? '#37a7ff' : '#8b91a6';
}
export function kwStatusColor(s: string): string {
  return s === 'Published' ? '#22d3b0' : s === 'Targeting' ? '#37a7ff' : '#8b91a6';
}
export function prioStyle(p: string): { bg: string; fg: string } {
  const map: Record<string, [string, string]> = {
    High: ['rgba(255,93,108,.16)', '#ff5d6c'],
    Medium: ['rgba(239,162,58,.16)', '#efa23a'],
    Low: ['rgba(139,145,166,.16)', '#8b91a6'],
  };
  const [bg, fg] = map[p] || map.Medium;
  return { bg, fg };
}
export function rel(iso: string | null | undefined): string {
  return iso ? fmtRelativeDate(iso) : '—';
}
export function nfmt(n: number): string {
  return (n || 0).toLocaleString();
}

// ── score ring (SVG) ─────────────────────────────────────────────────────────
const C = 94.2; // circumference for r=15
export function ScoreRing({ score, size = 32, stroke = 4, showOutOf = false }: { score: number; size?: number; stroke?: number; showOutOf?: boolean }) {
  const fg = scoreColor(score);
  const dash = ((score / 100) * C).toFixed(1) + ' ' + C;
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox="0 0 36 36" style={{ transform: 'rotate(-90deg)' }}>
        <circle cx="18" cy="18" r="15" fill="none" stroke="var(--bd2)" strokeWidth={stroke} />
        <circle cx="18" cy="18" r="15" fill="none" stroke={fg} strokeWidth={stroke} strokeLinecap="round" strokeDasharray={dash} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span className="seo-ringval" style={{ color: 'var(--ink)', fontSize: Math.round(size * 0.3) }}>{score}</span>
        {showOutOf && <span style={{ fontSize: Math.round(size * 0.12), color: 'var(--soft)' }}>/100</span>}
      </div>
    </div>
  );
}

export function Pill({ label, bg, fg, dot = true }: { label: string; bg: string; fg: string; dot?: boolean }) {
  return (
    <span className="seo-pill" style={{ background: bg, color: fg }}>
      {dot && <span className="dot" />}
      {label}
    </span>
  );
}

// ── KPI cards row ────────────────────────────────────────────────────────────
export interface Kpi { k: string; icon: string; iconColor: string; v: ReactNode; sub: string; subColor: string; }
export function KpiCards({ kpis }: { kpis: Kpi[] }) {
  return (
    <div className="seo-kpis" style={{ marginBottom: 26 }}>
      {kpis.map((k) => (
        <div className="seo-kpi" key={k.k}>
          <div className="seo-kpi-k"><span style={{ color: k.iconColor }}>{k.icon}</span>{k.k}</div>
          <div className="seo-kpi-v">{k.v}</div>
          <div className="seo-kpi-sub" style={{ color: k.subColor }}>{k.sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── misc atoms ───────────────────────────────────────────────────────────────
export function Spinner() {
  return <div className="adm-loading" style={{ minHeight: 240 }}><div className="adm-spin" /></div>;
}
export function SearchIcon() {
  return (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--soft)" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
}
export function SparkIcon({ size = 15, color = '#fff' }: { size?: number; color?: string }) {
  return (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v3M12 18v3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M3 12h3M18 12h3M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" /></svg>);
}
export function PlusIcon() {
  return (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
}

// ── URL state (so refresh restores section / open article) ───────────────────
export function adminUrlGet(k: string): string | null {
  try { return new URLSearchParams(window.location.search).get(k); } catch { return null; }
}
export function adminUrlSet(patch: Record<string, string | null>): void {
  try {
    const u = new URL(window.location.href);
    for (const [k, v] of Object.entries(patch)) { if (v) u.searchParams.set(k, v); else u.searchParams.delete(k); }
    window.history.replaceState(null, '', u.pathname + u.search + u.hash);
  } catch { /* ignore */ }
}

// ── toast ────────────────────────────────────────────────────────────────────
export function useToast(): [ReactNode, (msg: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback((m: string) => {
    if (timer.current) window.clearTimeout(timer.current);
    setMsg(m);
    timer.current = window.setTimeout(() => setMsg(null), 2400);
  }, []);
  const node = msg ? (
    <div className="seo-toast">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--cy, #22d3b0)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
      {msg}
    </div>
  ) : null;
  return [node, show];
}
